import {
  Breadcrumb,
  BreadcrumbButton,
  BreadcrumbDivider,
  BreadcrumbItem,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const useStyles = makeStyles({
  bar: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXXL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

interface Crumb {
  label: string;
  /** Absolute path this crumb links to; omitted for the current (last) crumb. */
  to?: string;
}

/** Human labels for known top-level segments; unknown segments pass through. */
const SEGMENT_LABELS: Record<string, string> = {
  tenants: 'Tenants',
  users: 'Users',
  settings: 'Settings',
};

function labelFor(segment: string): string {
  return SEGMENT_LABELS[segment] ?? segment;
}

/**
 * Derive the breadcrumb trail from the current pathname. Always rooted at
 * "Dashboard" (`/`); each subsequent path segment becomes a cumulative crumb.
 */
export function buildCrumbs(pathname: string): Crumb[] {
  const segments = pathname.split('/').filter((s) => s.length > 0);
  const crumbs: Crumb[] = [{ label: 'Dashboard', to: '/' }];

  let acc = '';
  segments.forEach((segment) => {
    acc += `/${segment}`;
    crumbs.push({ label: labelFor(segment), to: acc });
  });

  // The final crumb represents the current page — drop its link.
  const last = crumbs[crumbs.length - 1];
  if (last !== undefined) {
    delete last.to;
  }
  return crumbs;
}

/** Route-driven Fluent breadcrumb rendered beneath the command bar. */
export function Breadcrumbs(): JSX.Element {
  const styles = useStyles();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const crumbs = buildCrumbs(pathname);

  return (
    <div className={styles.bar}>
      <Breadcrumb aria-label="Breadcrumb">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          const target = crumb.to;
          return (
            <Fragment key={`${crumb.label}-${index}`}>
              <BreadcrumbItem>
                <BreadcrumbButton
                  current={isLast}
                  onClick={
                    target === undefined
                      ? undefined
                      : () => {
                          void navigate(target);
                        }
                  }
                >
                  {crumb.label}
                </BreadcrumbButton>
              </BreadcrumbItem>
              {!isLast && <BreadcrumbDivider />}
            </Fragment>
          );
        })}
      </Breadcrumb>
    </div>
  );
}
